import React, { useState, useEffect,useRef } from 'react';
import { Menu, X, Phone, Mail, Clock, Wrench,Scissors, Home, AlertCircle, Zap, Star, Award, Shield, Calculator, Search,Truck, ArrowLeft, Package, CloudRain, ChevronRight, ArrowRight, Users, Building, CheckCircle, TrendingUp, ChevronLeft, Grid, List, ShoppingCart, HardHat, PaintBucket, Droplets, Thermometer, Layers, Send, Calendar, DollarSign, Bath, Target, Hammer, Handshake, Cpu, Microscope, Quote, MapPin} from 'lucide-react';

import { Trabajo, Producto, Servicio, PhoneNumber } from '../types';
import { apiCall, API_BASE_URL, formatPrecio } from '../utils';

// ==================== COMPONENTE PRINCIPAL DE PRODUCTOS ====================
export default function ProductosPage({ navigateToPage }: { navigateToPage?: (page: string) => void }) {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategoria, setSelectedCategoria] = useState<string>('todas');
  const [selectedProducto, setSelectedProducto] = useState<Producto | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showOnlyDestacados, setShowOnlyDestacados] = useState(false);

  const categorias = [
    { id: 'todas', label: 'Todos', icon: Package, color: 'gray' },
    { id: 'baño', label: 'Baño', icon: Bath, color: 'blue' },
    { id: 'piscina', label: 'Piscina', icon: Droplets, color: 'cyan' }
  ];

  useEffect(() => {
    fetchProductos();
  }, [selectedCategoria, showOnlyDestacados]);

  useEffect(() => {
    // Listener para cuando se selecciona un producto desde el buscador
    const handleSelectProducto = (event: CustomEvent) => {
      openModal(event.detail);
    };

    window.addEventListener('selectProducto', handleSelectProducto as EventListener);
    return () => {
      window.removeEventListener('selectProducto', handleSelectProducto as EventListener);
    };
  }, []);

  const fetchProductos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // ✅ URL CORREGIDA - sin duplicar /api
      let url = `${API_BASE_URL}/productos`;
      const params = new URLSearchParams();
      
      if (selectedCategoria !== 'todas') {
        params.append('categoria', selectedCategoria);
      }
      
      if (showOnlyDestacados) {
        params.append('destacados', 'true');
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setProductos(data);
      } else {
        console.error('❌ La respuesta no es un array:', data);
        setProductos([]);
      }
    } catch (error) {
      console.error('❌ Error al cargar productos:', error);
      setError('Error al cargar los productos. Por favor, verifica que el servidor esté funcionando.');
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar productos por búsqueda mejorada
  const productosFiltrados = productos.filter(producto => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const nombreLower = producto.nombre.toLowerCase();
    const descripcionLower = producto.descripcion.toLowerCase();
    
    // Para términos cortos 
    if (searchTerm.length <= 2) {
      const words = nombreLower.split(/\s+/);
      return words.some(word => word.startsWith(searchLower));
    }
    
    // Búsqueda normal
    return nombreLower.includes(searchLower) || 
          descripcionLower.includes(searchLower) ||
          producto.categoria.toLowerCase().includes(searchLower);
  });

  const openModal = (producto: Producto) => {
    setSelectedProducto(producto);
  };

  const closeModal = () => {
    setSelectedProducto(null);
  };

  // ✅ Bloquear el scroll de la página de fondo mientras el modal está abierto,
  // para que al deslizar dentro no se mueva el listado de detrás (bug en móvil).
  useEffect(() => {
    if (selectedProducto) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [selectedProducto]);

  // ✅ Cerrar el modal con la tecla Escape
  useEffect(() => {
    if (!selectedProducto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedProducto]);

  // Estadísticas de productos
  const stats = {
    total: productosFiltrados.length,
    destacados: productosFiltrados.filter(p => p.destacado).length,
    categorias: 2, // <- Fijo a 2 (baño y piscina)
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section Mejorado */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-32 overflow-hidden">
        {/* Imagen de fondo profesional */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=1920"
            alt="Almacén de materiales de construcción"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/40"></div>
        </div>
        
        {/* Patrón decorativo sutil */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Elementos flotantes decorativos */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
        </div>

        {/* Contenido principal */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Catálogo de <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Productos</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Todo lo que necesitas para tu proyecto de construcción con la mejor relación calidad-precio
          </p>
          
          {/* Mini estadísticas - OPCIÓN ALTERNATIVA */}
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto mt-12">
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20 hover:bg-white/15 transition-all">
              <p className="text-3xl font-bold text-white">{stats.total}</p>
              <p className="text-sm text-gray-300">Total Productos</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20 hover:bg-white/15 transition-all">
              <p className="text-3xl font-bold text-yellow-400">⭐ {stats.destacados}</p>
              <p className="text-sm text-gray-300">Destacados</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20 hover:bg-white/15 transition-all">
              <p className="text-3xl font-bold text-green-400">2</p>
              <p className="text-sm text-gray-300">Categorías</p>
            </div>
          </div>
        </div>
      </section>

      {/* Barra de búsqueda y filtros mejorada */}
      <section className="sticky top-20 z-40 bg-white shadow-lg py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Búsqueda y controles */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
                <label htmlFor="search-input" className="sr-only">Buscar productos por nombre o descripción</label>
                <input
                  id="search-input"
                  type="text"
                  placeholder="Buscar productos por nombre o descripción..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Controles de vista y filtros */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowOnlyDestacados(!showOnlyDestacados)}
                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center ${
                  showOnlyDestacados
                    ? 'bg-yellow-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Star className="h-4 w-4 mr-2" />
                Destacados
              </button>
              
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-600'
                  }`}
                  aria-label="Ver productos en cuadrícula"
                  aria-pressed={viewMode === 'grid'}
                >
                  <Grid className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-600'
                  }`}
                  aria-label="Ver productos en lista"
                  aria-pressed={viewMode === 'list'}
                >
                  <List className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Categorías con diseño mejorado */}
          <div className="flex flex-wrap gap-2">
            {categorias.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategoria(cat.id)}
                  className={`group relative px-4 py-2.5 rounded-lg font-medium transition-all duration-300 flex items-center ${
                    selectedCategoria === cat.id
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <Icon className={`h-4 w-4 mr-2 ${
                    selectedCategoria === cat.id ? 'animate-pulse' : ''
                  }`} />
                  {cat.label}
                  {selectedCategoria === cat.id && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full animate-ping"></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contenido principal */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-600 absolute top-0"></div>
              </div>
              <p className="mt-4 text-gray-600">Cargando productos...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Error al cargar productos</h3>
              <p className="text-red-600 mb-6">{error}</p>
              <button
                onClick={fetchProductos}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Reintentar
              </button>
            </div>
          )}

          {!loading && !error && productosFiltrados.length === 0 && (
            <div className="text-center py-20">
              <Package className="h-20 w-20 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                No se encontraron productos
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                {searchTerm 
                  ? `No hay productos que coincidan con "${searchTerm}"`
                  : selectedCategoria !== 'todas'
                    ? `No hay productos en la categoría "${categorias.find(c => c.id === selectedCategoria)?.label}"`
                    : 'No hay productos disponibles en este momento'
                }
              </p>
              {(searchTerm || selectedCategoria !== 'todas') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategoria('todas');
                  }}
                  className="mt-4 text-blue-600 font-medium hover:text-blue-700"
                >
                  Ver todos los productos
                </button>
              )}
            </div>
          )}

          {!loading && !error && productosFiltrados.length > 0 && (
            <>
              {/* Contador de resultados */}
              <div className="mb-6 text-gray-600">
                Mostrando <span className="font-semibold text-gray-900">{productosFiltrados.length}</span> productos
                {searchTerm && ` para "${searchTerm}"`}
              </div>

              {/* Grid o Lista de productos */}
              <div className={
                viewMode === 'grid' 
                  ? "grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
                  : "space-y-4"
              }>
                {productosFiltrados.map((producto) => (
                  <div
                    key={producto.id}
                    id={`producto-${producto.id}`}
                    onClick={() => openModal(producto)}
                    className={`bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${
                      viewMode === 'list' ? 'flex' : ''
                    }`}
                  >
                    {/* Imagen */}
                    <div className={`relative overflow-hidden bg-[#f0f0f0] ${
                      viewMode === 'list' ? 'w-48' : 'h-64'
                    }`}>
                      {producto.imagen_url ? (
                        <img
                          src={producto.imagen_url}
                          alt={producto.nombre}
                          loading="lazy"
                          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/imagen-no-disponible.svg';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-20 w-20 text-gray-400" />
                        </div>
                      )}
                      
                      {/* Badges mejorados */}
                      <div className="absolute top-3 right-3 space-y-2">
                        {producto.destacado && (
                          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center shadow-lg">
                            <Star className="h-3 w-3 mr-1" />
                            Destacado
                          </div>
                        )}
                        <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                          {producto.categoria}
                        </div>
                      </div>

                      {/* Precio */}
                      <div className="absolute bottom-0 left-0 right-0">
                        <div className="bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent pt-8 pb-3 px-4">
                          <p className="text-xl font-bold text-white drop-shadow-md">{formatPrecio(producto.precio)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Contenido */}
                    <div className={`p-5 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                      <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {producto.nombre}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {producto.descripcion}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <button className="text-blue-600 font-medium text-sm flex items-center group-hover:text-blue-700">
                          Ver detalles
                          <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Sección de Catálogo Informativo */}
              <div className="mt-16 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden border border-slate-700">
                <div className="absolute inset-0 bg-blue-900/20 pattern-grid opacity-20"></div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/20 mb-6">
                    <Building className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4">Catálogo Informativo de Tienda Física</h3>
                  <p className="text-lg mb-6 text-gray-300">
                    Esta sección muestra una selección de los artículos disponibles en nuestro almacén. 
                    <strong className="text-white"> No realizamos venta online directa.</strong> 
                  </p>
                  <p className="text-md text-gray-400 mb-8 max-w-2xl mx-auto">
                    Si está buscando un producto específico de baño y no lo encuentra aquí, 
                    no dude en consultarnos. Trabajamos con catálogos extensos de primeras marcas y 
                    podemos conseguir lo que necesita.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      onClick={() => navigateToPage && navigateToPage('contacto')}
                      className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30"
                    >
                      Contactar para consultar
                    </button>
                    <a
                      href="tel:+34953039567"
                      className="border-2 border-slate-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-colors inline-flex items-center justify-center"
                    >
                      <Phone className="mr-2 h-5 w-5 text-blue-400" aria-hidden="true" />
                      Llamar Ahora
                    </a>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

        {/* Modal de Producto */}
        {selectedProducto && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-product-title"
            onClick={closeModal}
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-stretch sm:items-center justify-center z-[70] sm:p-4"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white sm:rounded-2xl w-full sm:max-w-4xl h-full sm:h-auto sm:max-h-[92vh] overflow-y-auto shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-gray-100 sticky top-0 bg-white z-20">
                <div className="min-w-0">
                  <span className="inline-block text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1 capitalize">
                    {selectedProducto.categoria}
                  </span>
                  <h2 id="modal-product-title" className="text-xl sm:text-2xl font-bold text-gray-900">{selectedProducto.nombre}</h2>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                  aria-label="Cerrar detalles del producto"
                >
                  <X className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              {/* Contenido */}
              <div className="grid md:grid-cols-2 gap-8 p-6 sm:p-8">
                {/* Columna izquierda - Imagen */}
                <div className="space-y-4">
                  <div className="bg-[#f0f0f0] rounded-xl overflow-hidden aspect-square border border-gray-200">
                    {selectedProducto.imagen_url ? (
                      <img
                        src={selectedProducto.imagen_url}
                        alt={selectedProducto.nombre}
                        className="w-full h-full object-contain p-4"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-32 w-32 text-gray-400" aria-hidden="true" />
                      </div>
                    )}
                  </div>

                  {selectedProducto.destacado && (
                    <span className="inline-flex items-center bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1.5 rounded-lg text-sm font-semibold">
                      <Star className="h-4 w-4 mr-2 fill-amber-500 text-amber-500" aria-hidden="true" />
                      Producto destacado
                    </span>
                  )}
                </div>

                {/* Columna derecha - Información */}
                <div className="space-y-6">
                  {/* Precio */}
                  <div className="border border-gray-200 rounded-xl p-5 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Precio</p>
                    <p className="text-4xl font-bold text-gray-900">{formatPrecio(selectedProducto.precio)}</p>
                    <p className="text-sm text-gray-500 mt-1">IVA incluido</p>
                  </div>

                  {/* Descripción */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Descripción</h3>
                    <p className="text-gray-700 leading-relaxed">{selectedProducto.descripcion}</p>
                  </div>

                  {/* Especificaciones técnicas */}
                  {selectedProducto.especificaciones && typeof selectedProducto.especificaciones === 'object' && Object.keys(selectedProducto.especificaciones).length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Especificaciones técnicas</h3>
                      <dl className="divide-y divide-gray-100 border-t border-gray-100">
                        {Object.entries(selectedProducto.especificaciones).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-start gap-4 py-3">
                            <dt className="text-gray-500 text-sm capitalize">
                              {key.replace(/_/g, ' ')}
                            </dt>
                            <dd className="text-gray-900 font-medium text-sm text-right">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}

                  {/* CTA: disponible en tienda física */}
                  <div className="border-t border-gray-100 pt-6">
                    <div className="bg-blue-50 rounded-xl p-5">
                      <h4 className="font-bold text-gray-900 mb-1">¿Te interesa este producto?</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Disponible en nuestra tienda de Martos. Consúltanos disponibilidad y condiciones.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <a
                          href="tel:+34953039567"
                          className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
                        >
                          <Phone className="h-5 w-5 mr-2" aria-hidden="true" />
                          Llamar
                        </a>
                        <a
                          href={`https://wa.me/34650441396?text=${encodeURIComponent(`Hola, me interesa el producto: ${selectedProducto.nombre}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 border border-green-600 text-green-700 py-3 px-4 rounded-lg font-semibold hover:bg-green-50 transition-colors flex items-center justify-center"
                        >
                          <svg viewBox="0 0 32 32" className="h-5 w-5 mr-2 fill-green-600" aria-hidden="true">
                            <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.6 4.463 1.732 6.404L3.2 28.8l6.567-1.72a12.73 12.73 0 0 0 6.237 1.588h.005c7.06 0 12.79-5.74 12.79-12.8 0-3.42-1.33-6.633-3.748-9.05a12.71 12.71 0 0 0-9.047-3.618zm0 23.31h-.004a10.6 10.6 0 0 1-5.4-1.48l-.388-.23-3.898 1.021 1.04-3.8-.253-.39a10.56 10.56 0 0 1-1.622-5.63c0-5.86 4.767-10.63 10.63-10.63 2.84 0 5.508 1.107 7.514 3.116a10.56 10.56 0 0 1 3.11 7.52c0 5.86-4.768 10.628-10.628 10.628zm5.83-7.96c-.32-.16-1.888-.932-2.18-1.038-.293-.107-.506-.16-.72.16-.213.32-.826 1.038-1.013 1.252-.186.213-.373.24-.692.08-.32-.16-1.35-.497-2.57-1.586-.95-.847-1.59-1.894-1.777-2.214-.187-.32-.02-.493.14-.652.144-.143.32-.373.48-.56.16-.187.213-.32.32-.534.107-.213.054-.4-.026-.56-.08-.16-.72-1.734-.986-2.374-.26-.623-.523-.54-.72-.55l-.612-.01c-.214 0-.56.08-.854.4-.293.32-1.12 1.093-1.12 2.667s1.146 3.093 1.306 3.307c.16.213 2.256 3.443 5.464 4.828.764.33 1.36.527 1.824.674.767.244 1.464.21 2.016.127.615-.092 1.888-.772 2.155-1.518.266-.746.266-1.386.186-1.519-.08-.133-.293-.213-.613-.373z" />
                          </svg>
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
